import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  inject,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatListModule } from '@angular/material/list';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { Router, RouterModule } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TUI_IS_MOBILE } from '@taiga-ui/cdk';
import { TuiCountryIsoCode } from '@taiga-ui/i18n';
import { TUI_COUNTRIES_MASKS } from '@taiga-ui/kit';
import { ConditionsComponent } from 'src/app/features/about/conditions/conditions.component';
import { HelpComponent } from 'src/app/features/about/help/help.component';
import { CountriesModalComponent } from 'src/app/features/rates/countries-modal.component';
import { RatesModalComponent } from 'src/app/features/rates/rates-modal.component';
import { Rate, RatesModal } from 'src/app/features/rates/rates.model';
import { getCountryInfo } from 'src/app/features/recharges/recharges.countries';
import { AWSLambda } from 'src/app/AWSLambda.service';
import { AuthService } from 'src/app/services/auth.service';

@Component({
  selector: 'app-menu',
  templateUrl:"menu.component.html",
  imports: [CommonModule, MatListModule, RouterModule, TranslateModule, MatIconModule, MatProgressSpinnerModule, MatSnackBarModule],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MenuComponent {
  auth = inject(AuthService);
  router = inject(Router);
  dialog = inject(MatDialog);
  lambda = inject(AWSLambda);
  private cdr = inject(ChangeDetectorRef);
  private snackBar = inject(MatSnackBar);
  private translate = inject(TranslateService);
  window= window
  recargaOpen = false;
  tarifaOpen = false;
  loadingRates = false;

  constructor(
    @Inject(TUI_IS_MOBILE) readonly isMobile: boolean,
    @Inject(TUI_COUNTRIES_MASKS) private readonly countriesMasks: Record<TuiCountryIsoCode, string>,
  ) {}

  async signOut() {
    await this.auth.logout();
  }

  openConditions() {
    this.dialog.open(ConditionsComponent, {
      width: this.isMobile ? '95%' : '60%',
      height: '90%',
      panelClass: 'zendy-modal',
    });
  }

  openHelp() {
    this.dialog.open(HelpComponent, {
      width: this.isMobile ? '95%' : '60%',
      height: '90%',
      panelClass: 'zendy-modal',
    });
  }
  
  toggleRecarga() {
    this.recargaOpen = !this.recargaOpen;
  }

  toggleTarifa() {
    this.tarifaOpen = !this.tarifaOpen;
  }

  async openCallRates() {
    if (this.loadingRates) return;
    this.loadingRates = true;
    this.cdr.markForCheck();
    try {
      const res = await this.lambda.getLambdaFunction('lambda', '/get/contries', {
        queryStringParameters: { method: 'allowedToCall' },
      });
      if (!res.ok) return;

      const filteredCountries: TuiCountryIsoCode[] = (res.countries as string[] || [])
        .map((code: string) => code.toUpperCase())
        .filter((code: string) => Object.values(TuiCountryIsoCode).includes(code as TuiCountryIsoCode))
        .map((code: string) => code as TuiCountryIsoCode);

      this.openCountriesDialog(filteredCountries);
    } catch (error) {
      console.error('Error al obtener países:', error);
    } finally {
      this.loadingRates = false;
      this.cdr.markForCheck();
    }
  }

  private openCountriesDialog(countries: TuiCountryIsoCode[]) {
    this.dialog
      .open(CountriesModalComponent, {
        width: this.isMobile ? '85%' : '30%',
        panelClass: 'zendy-modal',
        data: countries,
      })
      .afterClosed()
      .subscribe(async (countryCode: TuiCountryIsoCode) => {
        if (!countryCode) return;
        const snackRef = this.snackBar.open(
          this.translate.instant('COMPONENTS.MENU.LOADING_RATES'),
          undefined,
          { duration: 0, panelClass: 'zendy-snackbar' }
        );
        const rates: Rate | undefined = await this.getPricing(countryCode);
        snackRef.dismiss();
        if (!rates) return;
        const data: RatesModal = {
          country: countryCode,
          pricing: rates.pricing,
          prefix: rates.to,
          onBack: () => this.openCountriesDialog(countries),
        };
        this.dialog.open(RatesModalComponent, {
          minWidth: this.isMobile ? '95%' : '30%',
          panelClass: 'zendy-modal',
          data,
        });
      });
  }

  private async getPricing(to: TuiCountryIsoCode): Promise<Rate | undefined> {
    if (!this.countriesMasks[to]) return undefined;
    const countryCode = this.countriesMasks[to].replace(/[()\s#-]/g, '');
    const isoCode = getCountryInfo(countryCode, to, 'ISO-3');
    return await this.lambda.getLambdaFunction('lambda', '/pricing/calls', {
      queryStringParameters: { from: '+34', to: countryCode, via: 'signalwire', filter: isoCode },
    });
  }
}
